export type DockPanelKey =
  | "annotation_timeline"
  | "annotation_list"
  | "event_detection"
  | "spectrogram"
  | "trend_correlation"
  | "psd_analysis"
  | "qeeg_trends"
  | "connectivity_analysis"
  | "hfo_analysis"
  | "live_scalp"
  | "ieeg_localize"
  | "artifact_quality"
  | "clinical_timeline"
  | "review_accel"
  | "clinical_collab";

/** Dock panels superseded by the unified bottom navigation strip (Phase 5C). */
export const RETIRED_DOCK_PANEL_KEYS: ReadonlySet<DockPanelKey> = new Set([
  "annotation_timeline",
  "trend_correlation",
]);

export const ACTIVE_DOCK_PANEL_ORDER: DockPanelKey[] = [
  "annotation_list",
  "clinical_timeline",
  "event_detection",
  "artifact_quality",
  "qeeg_trends",
  "spectrogram",
  "psd_analysis",
  "connectivity_analysis",
  "hfo_analysis",
  "live_scalp",
  "ieeg_localize",
  "review_accel",
  "clinical_collab",
];

export const ANALYSIS_DOCK_RAIL_PANEL_KEYS: DockPanelKey[] = [
  "annotation_list",
  "clinical_timeline",
  "event_detection",
  "artifact_quality",
  "qeeg_trends",
  "spectrogram",
  "psd_analysis",
  "connectivity_analysis",
  "hfo_analysis",
  "live_scalp",
  "ieeg_localize",
  "review_accel",
  "clinical_collab",
];

export const DOCK_PANEL_RAIL_MODIFIER: Partial<Record<DockPanelKey, string>> = {
  annotation_list: "events",
  event_detection: "events",
  clinical_timeline: "events",
  qeeg_trends: "trends",
  artifact_quality: "events",
  review_accel: "events",
  clinical_collab: "events",
  trend_correlation: "trends",
  spectrogram: "spectrogram",
  psd_analysis: "psd",
  connectivity_analysis: "trends",
  hfo_analysis: "trends",
  live_scalp: "spectrogram",
  ieeg_localize: "spectrogram",
};

export const DOCK_PANEL_LABELS: Record<DockPanelKey, string> = {
  annotation_timeline: "Timeline",
  /** Catalog of saved annotations/findings in the recording (not the Annotate workflow). */
  annotation_list: "Event Catalog",
  event_detection: "AI Triage",
  clinical_timeline: "Clinical Timeline",
  qeeg_trends: "Trends",
  artifact_quality: "Artifacts",
  review_accel: "Review Toolkit",
  clinical_collab: "Review Collab",
  trend_correlation: "Trend Correlation",
  spectrogram: "Spectrogram",
  psd_analysis: "PSD & Bands",
  connectivity_analysis: "Connectivity",
  hfo_analysis: "HFO",
  live_scalp: "Live Scalp",
  ieeg_localize: "iEEG Localize",
};

/** Vertical rail control that toggles the Trend/Event Dock sidebar. */
export const RIGHT_DOCK_RAIL_LABEL = "Event Dock";
export const RIGHT_DOCK_RAIL_LABEL_COMPACT = "Dock";

const DOCK_PANEL_LABELS_COMPACT: Partial<Record<DockPanelKey, string>> = {
  annotation_list: "Catalog",
  event_detection: "AI",
  clinical_timeline: "Timeline",
  qeeg_trends: "Trends",
  artifact_quality: "Artifacts",
  review_accel: "Toolkit",
  clinical_collab: "Collab",
  trend_correlation: "Trend",
  spectrogram: "Spec",
  psd_analysis: "PSD",
  connectivity_analysis: "Conn",
  hfo_analysis: "HFO",
  live_scalp: "Scalp",
  ieeg_localize: "iEEG",
};

export function getRightDockRailLabel(options?: { compact?: boolean }): string {
  return options?.compact ? RIGHT_DOCK_RAIL_LABEL_COMPACT : RIGHT_DOCK_RAIL_LABEL;
}

export function getDockPanelLabel(
  panelKey: DockPanelKey,
  options?: { compact?: boolean },
): string {
  if (options?.compact) {
    return DOCK_PANEL_LABELS_COMPACT[panelKey] ?? DOCK_PANEL_LABELS[panelKey];
  }
  return DOCK_PANEL_LABELS[panelKey];
}

export function isDockPanelKey(value: string): value is DockPanelKey {
  return Object.prototype.hasOwnProperty.call(DOCK_PANEL_LABELS, value);
}

export function isActiveDockPanelKey(panelKey: DockPanelKey): boolean {
  return !RETIRED_DOCK_PANEL_KEYS.has(panelKey);
}

export function normalizeDockPanelOrder(order: DockPanelKey[]): DockPanelKey[] {
  const validPanels = new Set<DockPanelKey>(ACTIVE_DOCK_PANEL_ORDER);
  const normalized = order.filter(
    (panelKey) => isActiveDockPanelKey(panelKey) && validPanels.has(panelKey),
  );
  const deduped = Array.from(new Set(normalized));
  ACTIVE_DOCK_PANEL_ORDER.forEach((panelKey) => {
    if (!deduped.includes(panelKey)) {
      deduped.push(panelKey);
    }
  });
  return deduped;
}
